import React, { useMemo, useState } from 'react';
import { IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import type { CustomerIndexItemDto, M365Tenant } from '@techgitter/contracts';
import { SidePanel } from '../panel/SidePanel';
import { CustomerLookupControl } from '../tickets/Customer/CustomerLookupControl';
import { MAP_SOURCE_LABELS } from '../../utils/m365TenantDisplay';
import './M365TenantMappingPanel.css';

interface M365TenantMappingPanelProps {
  tenant: M365Tenant;
  busy?: boolean;
  onClose: () => void;
  /** Bind the tenant to this customer (recorded as a manual, confirmed mapping). */
  onSave: (customerId: string) => void;
  /** Unbind the customer. Also confirmed, or tonight's reconcile re-binds it. */
  onClear: () => void;
}

/**
 * Right panel for deciding one tenant's customer (requirements §11.5).
 *
 * The panel leads with the tenant's domains because **the domain is the
 * evidence** — the FR2 reconciler matches on them, and they are what an operator
 * recognises a tenant by. A tenant name that differs from the customer name is
 * normal (holding companies, sole traders, trading names), so nothing here warns
 * about that; only the domains are worth reading.
 *
 * Saving always confirms. That is the whole point of the feature: the reconciler
 * re-decides any unconfirmed mapping on its next nightly pass, so a correction
 * that did not confirm would be silently reverted overnight.
 */
export const M365TenantMappingPanel: React.FC<M365TenantMappingPanelProps> = ({
  tenant,
  busy = false,
  onClose,
  onSave,
  onClear,
}) => {
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);

  const domains = useMemo(() => tenant.domains ?? [], [tenant.domains]);
  const hasExistingCustomer = Boolean(tenant.customerId);
  const isRebind =
    hasExistingCustomer &&
    customer !== null &&
    customer.dataverseCustomerId !== tenant.customerId;

  return (
    <div className="m365-map-panel">
      <SidePanel
        title="Map tenant to customer"
        submitLabel="Save mapping"
        isValid={!busy && customer !== null}
        onClose={onClose}
        onSubmit={() => customer && onSave(customer.dataverseCustomerId)}
        onDelete={hasExistingCustomer ? onClear : undefined}
        deleteLabel="Clear mapping"
        deleteDisabled={busy}
      >
        {/* Identity first: this is how an operator tells which tenant they are
            looking at without opening CIPP. */}
        <div className="m365-map-panel__tenant">
          <div className="m365-map-panel__tenant-name">
            {tenant.displayName || tenant.tenantId}
          </div>
          {tenant.nativeDomain && (
            <div className="m365-map-panel__tenant-native">{tenant.nativeDomain}</div>
          )}
        </div>

        <div className="m365-map-panel__section">
          <div className="m365-map-panel__section-title">Domains</div>
          {domains.length === 0 ? (
            <p className="m365-map-panel__empty">This tenant reports no domains.</p>
          ) : (
            <ul className="m365-map-panel__domains">
              {domains.map((d) => (
                <li key={d} className="m365-map-panel__domain">
                  {d}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="m365-map-panel__section">
          <div className="m365-map-panel__section-title">Current mapping</div>
          {hasExistingCustomer ? (
            <>
              <div className="m365-map-panel__current">
                {tenant.customerName || tenant.customerId}
              </div>
              <div className="m365-map-panel__meta">
                {`Matched via ${MAP_SOURCE_LABELS[tenant.mapSource] ?? tenant.mapSource}`}
                {tenant.matchedOn ? ` on ${tenant.matchedOn}` : ''}
                {tenant.mapConfirmed ? ' · confirmed' : ' · not yet confirmed'}
              </div>
            </>
          ) : (
            <div className="m365-map-panel__meta">
              No customer — this tenant syncs nothing until one is chosen.
            </div>
          )}
        </div>

        <div className="m365-map-panel__section">
          <div className="m365-map-panel__section-title">
            {hasExistingCustomer ? 'Change to' : 'Customer'}
          </div>
          <CustomerLookupControl value={customer} onChange={setCustomer} disabled={busy} />
          <p className="m365-map-panel__hint">
            Search by name, email or domain. A customer can own several tenants, so
            picking one that already has a tenant is fine.
          </p>
        </div>

        {isRebind && (
          <div className="m365-map-panel__warning">
            <IconAlertTriangle size={14} stroke={1.5} />
            <span>
              Re-points this tenant’s users from{' '}
              <strong>{tenant.customerName || 'the current customer'}</strong> to{' '}
              <strong>{customer?.displayName}</strong>.
            </span>
          </div>
        )}

        <div className="m365-map-panel__confirm-note">
          <IconCheck size={14} stroke={1.5} />
          <span>
            Saving marks this mapping confirmed, so the nightly reconcile leaves it
            alone.
          </span>
        </div>
      </SidePanel>
    </div>
  );
};
